import api from './api'
import type {
  LoginCredentials,
  RegisterCredentials,
  AuthResponse,
  User,
  MessageResponse,
  EmailVerificationRequest,
  ResendVerificationRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  UserProfileUpdate,
  AvatarUploadUrlRequest,
  AvatarUploadUrlResponse,
  ChangePasswordRequest,
  PasswordChangedResponse
} from '@/types/auth'

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/login', credentials)
    return response.data
  },

  async register(credentials: RegisterCredentials): Promise<MessageResponse> {
    const { confirmPassword, ...data } = credentials
    const response = await api.post<MessageResponse>('/auth/register', data)
    return response.data
  },

  async exchangeOAuthCode(code: string): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/oauth/google/exchange', { code })
    return response.data
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get<User>('/users/me')
    return response.data
  },

  async verifyEmail(request: EmailVerificationRequest): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/verify-email', request)
    return response.data
  },

  async resendVerification(request: ResendVerificationRequest): Promise<MessageResponse> {
    const response = await api.post<MessageResponse>('/auth/resend-verification', request)
    return response.data
  },

  async forgotPassword(data: ForgotPasswordRequest): Promise<MessageResponse> {
    const response = await api.post<MessageResponse>('/auth/forgot-password', data)
    return response.data
  },

  async resetPassword(data: ResetPasswordRequest): Promise<MessageResponse> {
    const response = await api.post<MessageResponse>('/auth/reset-password', data)
    return response.data
  },

  getGoogleLoginUrl(): string {
    const baseUrl = api.defaults.baseURL || ''
    return `${baseUrl}/oauth/google/login`
  },

  async updateProfile(data: UserProfileUpdate): Promise<User> {
    const response = await api.patch<User>('/users/me', data)
    return response.data
  },

  async requestAvatarUploadUrl(data: AvatarUploadUrlRequest): Promise<AvatarUploadUrlResponse> {
    const response = await api.post<AvatarUploadUrlResponse>('/users/me/avatar/upload-url', data)
    return response.data
  },

  async confirmAvatar(s3Key: string): Promise<User> {
    const response = await api.post<User>('/users/me/avatar/confirm', { s3_key: s3Key })
    return response.data
  },

  async deleteAvatar(): Promise<User> {
    const response = await api.delete<User>('/users/me/avatar')
    return response.data
  },

  async changePassword(data: ChangePasswordRequest): Promise<PasswordChangedResponse> {
    const response = await api.post<PasswordChangedResponse>('/users/me/password', data)
    return response.data
  },

  /** Emails a link to set a first password; a signed-in session alone can't set one. */
  async requestPasswordSetLink(): Promise<MessageResponse> {
    const response = await api.post<MessageResponse>('/users/me/password/set')
    return response.data
  },

  async disconnectGoogle(): Promise<User> {
    const response = await api.post<User>('/users/me/disconnect-google')
    return response.data
  },

  async deleteAccount(): Promise<MessageResponse> {
    const response = await api.delete<MessageResponse>('/users/me')
    return response.data
  }
}
